/**
 * The URL that may leave for Google Analytics (21/09).
 *
 * Several of this app's URLs carry things that must never reach a third party:
 * an e-mail address (`/activate-account?resend=<email>`), one-time credentials
 * (`?token_hash=`, `?token=`, the signup `?draft=` resume token, Stripe's
 * `?session_id=`), tokens in the path (`/onboarding/<token>`,
 * `/complete/<token>`) and Supabase's session in the fragment.
 *
 * So the query goes through an ALLOW-list, never a deny-list — the same lesson
 * as the log redactor (`redaction.util.ts`): a list of what to hide only covers
 * the names someone remembered to add, and the next screen adds a new one.
 * Only campaign attribution survives, because it is what carries the visit from
 * the website into the app.
 */
const KEPT_QUERY_PARAMS = new Set([
	'utm_source',
	'utm_medium',
	'utm_campaign',
	'utm_term',
	'utm_content',
	'utm_id',
	'gclid',
	'gbraid',
	'wbraid',
	'dclid',
	// GA4's cross-domain linker. Harmless here (web and app share the cookie
	// domain) but stripping it would break the day a second domain is added.
	'_gl',
]);

/** Route segments whose next segment is a secret, not a page. */
const TOKEN_BEFORE = new Set(['onboarding', 'complete']);

/**
 * A path fit for GA4 reports: token segments masked, and numeric ids folded to
 * `:id` so `/company/talent/412` and `/company/talent/97` count as one page —
 * the report is about screens, and a candidate's row id is not a screen.
 */
export function sanitizeAnalyticsPath(pathname: string): string {
	const segments = pathname.split('/');
	return segments
		.map((segment, index) => {
			if (index > 0 && TOKEN_BEFORE.has(segments[index - 1]) && segment) return ':token';
			if (/^\d+$/.test(segment)) return ':id';
			return segment;
		})
		.join('/');
}

/** The full `page_location`: origin + sanitised path + kept params, never the fragment. */
export function sanitizeAnalyticsLocation(href: string): string {
	let url: URL;
	try {
		url = new URL(href);
	} catch {
		return '';
	}
	const kept = new URLSearchParams();
	for (const [key, value] of url.searchParams) {
		if (KEPT_QUERY_PARAMS.has(key.toLowerCase())) kept.append(key, value);
	}
	const query = kept.toString();
	return `${url.origin}${sanitizeAnalyticsPath(url.pathname)}${query ? `?${query}` : ''}`;
}
